import { delay } from "@/utils/promise";
import { ApiClient } from "client/base";
import { createQueryParams } from "types/content_generation";
const rt_ais_connections = "v1/ai_service_connections";
const rt_ais_models = "v1/ai_service_models";
const rt_cg_collections = "v1/content_generation_collections";
const rt_cg_executions = "v1/content_generation_executions";
const rt_cg_results = "v1/content_generation_results";
const rt_ft_executions = "v1/fine_tuning_executions";
const rt_example_sets = "v1/content_generation_example_sets";
export class CloudContentGenerationClient extends ApiClient {
    /** Create a Connection and store the configuration in cloud storage. */
    createConnectionWithConfig(create) {
        return this.base.post(`${rt_ais_connections}`, create);
    }
    getConnections(query) {
        const params = createQueryParams(query);
        return this.base.get(`${rt_ais_connections}`);
    }
    getConnectionById(connectionId) {
        return this.base.get(`${rt_ais_connections}/${connectionId}`);
    }
    /** Get the Connection along with the configuration. */
    getConnectionWithConfig(id) {
        return this.base.get(`${rt_ais_connections}/${id}`);
    }
    /** Change the configuration on cloud storage for a Connection. */
    updateConnectionConfig(id, config) {
        return this.base.patch(`${rt_ais_connections}/${id}/config`, config);
    }
    /** Sets all associated Models and the Connection to DELETED and removes the
     * configurations from cloud storage.
     */
    deleteConnection(id) {
        return this.base.delete(`${rt_ais_connections}/${id}`);
    }
    /** Create a Model for a given Connection and store the configuration in cloud storage. */
    createModelWithConfig(connection_id, create) {
        return this.base.post(`${rt_ais_connections}/${connection_id}/models`, create);
    }
    /** Get the Model along with the configuration. */
    getModelWithConfig(id) {
        return this.base.get(`${rt_ais_models}/${id}`);
    }
    /** Get the status of a model. */
    getModelStatus(modelId) {
        return this.base.get(`${rt_ais_models}/${modelId}/status`);
    }
    /** Change the configuration on cloud storage for a Model. */
    updateModelConfig(id, config) {
        return this.base.patch(`${rt_ais_models}/${id}/config`, config);
    }
    /** Marks model as DELETED and removes the config from cloud storage. */
    deleteModel(id) {
        return this.base.delete(`${rt_ais_models}/${id}`);
    }
    getCollectionExecutions(id) {
        return this.base.get(`${rt_cg_collections}/${id}/executions`);
    }
    getCollectionMessages(id) {
        return this.base.get(`${rt_cg_collections}/${id}/messages`);
    }
    getExecution(id) {
        return this.base.get(`${rt_cg_executions}/${id}`);
    }
    createExecution(create) {
        return this.base.post(`${rt_cg_executions}`, create);
    }
    getResult(id) {
        return this.base.get(`${rt_cg_results}/${id}`);
    }
    createResult(executionId, create) {
        return this.base.post(`${rt_cg_executions}/${executionId}/results`, create);
    }
    addOutputFeedbackItem(id, item) {
        return this.base.post(`${rt_cg_results}/${id}/output_feedback`, item);
    }
    createFineTuningExecution(post) {
        return this.base.post(`${rt_ft_executions}`, post);
    }
    getExecutionCostEstimate(post) {
        return this.base.post(`${rt_ft_executions}/estimate_cost`, post);
    }
    getFineTuningExecution(executionId) {
        return this.base.get(`${rt_ft_executions}/${executionId}`);
    }
    cancelFineTuningExecution(executionId) {
        return this.base.delete(`${rt_ft_executions}/${executionId}`);
    }
    getFineTuningExecutionDetails(executionId) {
        return this.base.get(`${rt_ft_executions}/${executionId}/details`);
    }
    addCustomExampleSetData(exampleSetId) {
        return this.base.post(`${rt_example_sets}/${exampleSetId}/custom_data`);
    }
    generateExampleSetData(id, modelId, rowCount) {
        return this.base
            .post(`${rt_example_sets}/${id}/generate_data`, {
            model_id: modelId,
            row_count: rowCount
        })
            .then((response) => this._pollDataGenerationStatus(id, modelId, response.job_id));
    }
    _pollDataGenerationStatus(id, modelId, jobId) {
        return this._getDataGenerationStatus(id, modelId, jobId).then((response) => {
            if (response.status != "RUNNING") {
                return {
                    status: response.status
                };
            }
            return delay(5000).then(() => this._pollDataGenerationStatus(id, modelId, jobId));
        });
    }
    _getDataGenerationStatus(id, modelId, jobId) {
        return this.base.get(`${rt_example_sets}/${id}/generate_data/${jobId}/${modelId}`);
    }
}
